import type { MonsterDef } from '../../data/schema';
import { DEFAULT_BODY_HEIGHT_FACTOR } from '../config';
import type { Monster, Vec2 } from '../types';
import { pickSpawnPoints } from '../map/nav';
import { clampToWalkable, entitiesOfKind, getPlayer, spawn, type World } from '../world';

export function spawnMonster(world: World, def: MonsterDef, at: Vec2): Monster {
  const hp = Math.round(def.hp * world.hpMult);
  return spawn<Monster>(world, {
    kind: 'monster', team: 'enemy', defId: def.id, sprite: def.sprite,
    pos: { ...at }, vel: { x: 0, y: 0 }, radius: def.radius, height: def.height ?? def.radius * DEFAULT_BODY_HEIGHT_FACTOR, facing: { x: -1, y: 0 },
    hp, hpMax: hp, alive: true, anim: 'idle', animTime: 0,
    attackCooldown: 0.5, attackCooldownMax: def.attackCooldown, windup: 0, windupMax: def.attackWindup,
    range: def.attackRange ?? def.radius + 4, xp: def.xp, kamas: def.kamas, contactDamage: def.contactDamage,
    resistances: def.resistances, speed: world.monsterSpeedPct === 0 ? def.speed : def.speed * (1 + world.monsterSpeedPct / 100), slow: null, behavior: def.behavior, boss: def.boss,
    charge: def.behavior === 'charger' ? { phase: 'none', timer: 0, dir: { x: -1, y: 0 }, cooldown: def.chargeCooldown, hitDone: false } : null,
    poison: null,
  });
}

export function waveSystem(world: World): void {
  if (world.cleared) return;
  const waves = world.waves;
  if (!world.waveSpawned) {
    const wave = waves[world.wave];
    if (!wave) { world.cleared = true; world.events.push({ type: 'roomCleared' }); return; }
    let i = 0;
    const grid = world.room.grid;
    const player = getPlayer(world);
    const total = wave.monsters.reduce((n, g) => n + g.count, 0);
    // salle à grille : cellules marchables atteignables, entre 650 et 1 500 px du joueur (au moment de la vague)
    const points = grid && player ? pickSpawnPoints(grid, world.rng, player.pos, total) : null;
    for (const group of wave.monsters) {
      const def = world.content.monsters[group.id];
      if (!def) continue;
      for (let k = 0; k < group.count; k++, i++) {
        if (points && points.length) {
          // le boss entre plus près (450 à 850 px) : le combat commence sans longue traversée de la salle
          const p = def.boss && grid && player ? pickSpawnPoints(grid, world.rng, player.pos, 1, 450, 850)[0] ?? points[0]! : points[i % points.length]!;
          spawnMonster(world, def, clampToWalkable(p, def.radius, world.room));
          continue;
        }
        const sp = world.room.spawns[i % world.room.spawns.length]!;
        spawnMonster(world, def, { x: sp.x + world.rng.int(-30, 30), y: sp.y + world.rng.int(-30, 30) });
      }
    }
    world.waveSpawned = true;
    world.events.push({ type: 'waveStart', wave: world.wave + 1, total: waves.length });
    return;
  }
  const remaining = entitiesOfKind<Monster>(world, 'monster').filter((m) => m.alive).length;
  if (remaining === 0) { world.wave += 1; world.waveSpawned = false; }
}
